import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { ConfigValueItem } from 'constants/Constants'
import config from 'config'
import { useAuth0Authentication } from './useAuth0Authentication'

export interface IUsePipeMaterials {
    pipeMaterials: ConfigValueItem[] | undefined,
    isLoading: boolean,
    error: Error | null,
    deletePipeMaterial: (itemToDelete: ConfigValueItem) => Promise<void>,
    isDeleting: boolean,
    deleteError: Error | null,
    addPipeMaterial: (pipeMaterial: ConfigValueItem) => Promise<void>,
    isAdding: boolean,
    addError: Error | null,
    editPipeMaterial: (pipeMaterial: ConfigValueItem) => Promise<void>,
    isEditing: boolean,
    editError: Error | null
}

// Separate API function for better testability and reusability
const fetchPipeMaterials = async (accessToken: string): Promise<ConfigValueItem[]> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-materials`,
        {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to fetch pipe materials: ${response.statusText}`)
    }

    const pipeMaterialsResp: ConfigValueItem[] = await response.json()
    return pipeMaterialsResp.sort((a, b) => a.value.localeCompare(b.value))
}

// Separate API function for deleting pipe materials
const deletePipeMaterialAPI = async (accessToken: string, itemToDelete: ConfigValueItem): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-materials`,
        {
            method: 'DELETE',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(itemToDelete)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to delete pipe material: ${response.statusText}`)
    }
}

// Separate API function for adding pipe materials
const addPipeMaterialAPI = async (accessToken: string, pipeMaterial: ConfigValueItem): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-materials`,
        {
            method: 'POST',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(pipeMaterial)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to add pipe material: ${response.statusText}`)
    }
}

// Separate API function for editing pipe materials
const editPipeMaterialAPI = async (accessToken: string, pipeMaterial: ConfigValueItem): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-materials`,
        {
            method: 'PATCH',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(pipeMaterial)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to edit pipe material: ${response.statusText}`)
    }
}

export const usePipeMaterials = (): IUsePipeMaterials => {
   
    const { getAuth0AccessToken } = useAuth0Authentication()
    const queryClient = useQueryClient()

    const {
        data: pipeMaterials,
        isLoading,
        error
    } = useQuery<ConfigValueItem[], Error>({
        queryKey: ['pipeMaterials'],
        queryFn: async () => {
            const accessToken = await getAuth0AccessToken()
            return fetchPipeMaterials(accessToken)
        }
    })

    const {
        mutateAsync: deletePipeMaterial,
        isPending: isDeleting,
        error: deleteError
    } = useMutation<void, Error, ConfigValueItem>({
        mutationFn: async (itemToDelete: ConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return deletePipeMaterialAPI(accessToken, itemToDelete)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe materials after successful deletion
            queryClient.invalidateQueries({ queryKey: ['pipeMaterials'] })
        }
    })

    const {
        mutateAsync: addPipeMaterial,
        isPending: isAdding,
        error: addError
    } = useMutation<void, Error, ConfigValueItem>({
        mutationFn: async (pipeMaterial: ConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return addPipeMaterialAPI(accessToken, pipeMaterial)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe materials after successful addition
            queryClient.invalidateQueries({ queryKey: ['pipeMaterials'] })
        }
    })

    const {
        mutateAsync: editPipeMaterial,
        isPending: isEditing,
        error: editError
    } = useMutation<void, Error, ConfigValueItem>({
        mutationFn: async (pipeMaterial: ConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return editPipeMaterialAPI(accessToken, pipeMaterial)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe materials after successful edit
            queryClient.invalidateQueries({ queryKey: ['pipeMaterials'] })
        }
    })

    return {
        pipeMaterials,
        isLoading,
        error: error || null,
        deletePipeMaterial,
        isDeleting,
        deleteError: deleteError || null,
        addPipeMaterial,
        isAdding,
        addError: addError || null,
        editPipeMaterial,
        isEditing,
        editError: editError || null
    }
}